import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowLeftRight, Send, CreditCard, Users, History, 
  Shield, Settings, QrCode, Link2, Code, Grid3X3, Award, HelpCircle, 
  GraduationCap, LogOut, ChevronLeft, Bell, Copy, CheckCircle
} from "lucide-react";

// Crypto icons as simple components
const CryptoIcon = ({ symbol }) => {
  const icons = {
    USDT: { bg: '#26A17B', text: 'T' },
    BTC: { bg: '#F7931A', text: '₿' },
    LTC: { bg: '#345D9D', text: 'Ł' },
    BNB: { bg: '#F3BA2F', text: 'B' },
    ETH: { bg: '#627EEA', text: 'Ξ' },
    TRX: { bg: '#FF0013', text: 'T' },
    USDC: { bg: '#2775CA', text: '$' },
  };
  const icon = icons[symbol] || { bg: '#666', text: '?' };
  return (
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
      style={{ backgroundColor: icon.bg }}
    >
      {icon.text}
    </div>
  );
};

// Generate random values outside component
const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);
const generateBrowserTheme = () => Math.random() > 0.5 ? 'dark' : 'light';

export default function CashinoutMainPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [balances, setBalances] = useState({});
  const [browserTheme, setBrowserTheme] = useState(generateBrowserTheme);
  const [randomUserId] = useState(generateRandomId);

  useEffect(() => {
    document.title = 'Главная';
    
    // Check for theme in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      setBrowserTheme(themeParam);
    }
    
    // Set favicon
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/cashinout-favicon.ico';
  }, []);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dataParam));
        setData(parsed);
        // Generate random balances up to 500
        setBalances({
          USDT: (Math.random() * 500).toFixed(2),
          BTC: (Math.random() * 0.01).toFixed(6),
          LTC: (Math.random() * 2).toFixed(4),
          BNB: (Math.random() * 1).toFixed(4),
          ETH: (Math.random() * 0.2).toFixed(5),
          TRX: (Math.random() * 500).toFixed(2),
          USDC: (Math.random() * 500).toFixed(2),
        });
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.cashinoutId || randomUserId;

  // Theme colors
  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    border: isDark ? '#3c4043' : '#dadce0',
  };

  // Set body background to match theme to avoid dark strips in screenshots  useEffect(() => {    document.documentElement.style.backgroundColor = theme.bg;    document.body.style.backgroundColor = theme.bg;    document.body.style.margin = "0";    document.body.style.padding = "0";    return () => {      document.documentElement.style.backgroundColor = "";      document.body.style.backgroundColor = "";    };  }, [theme.bg]);
  // Browser frame component
  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        <div className="flex items-end flex-1 pl-2">
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.inactiveTabBg }}>
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
          </div>
          <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer" style={{ backgroundColor: theme.activeTabBg }}>
            <img src="/cashinout-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Главная</span>
          </div>
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          </button>
        </div>
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg></button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}><svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg></button>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg></button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>cashinout.io/dashboard</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg></button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}><svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg></button>
        </div>
      </div>
    </div>
  );

  const menuItems = [
    { icon: Home, label: 'Главная', active: true },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowLeftRight, label: 'Обмен', active: false },
    { icon: Send, label: 'Отправить', active: false },
    { icon: CreditCard, label: 'Виртуальные карты', active: false },
    { icon: Users, label: 'Реферальная программа', active: false },
    { icon: History, label: 'История', active: false },
    { icon: Shield, label: '2FA', active: false },
    { icon: Settings, label: 'Настройки', active: false },
  ];

  const appItems = [
    { icon: QrCode, label: 'QR для авторизации' },
  ];

  const businessItems = [
    { icon: Link2, label: 'Платёжные ссылки' },
    { icon: Code, label: 'API' },
    { icon: Grid3X3, label: 'Массовые выплаты' },
  ];

  const bottomItems = [
    { icon: Award, label: 'Тарифы' },
    { icon: HelpCircle, label: 'Служба поддержки' },
    { icon: GraduationCap, label: 'Академия Cashinout' },
    { icon: HelpCircle, label: 'Частые вопросы' },
    { icon: LogOut, label: 'Выйти' },
  ];

  const cryptoList = [
    { symbol: 'USDT', name: 'Tether' },
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'LTC', name: 'Litecoin' },
    { symbol: 'BNB', name: 'Binance Coin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'TRX', name: 'TRON' },
    { symbol: 'USDC', name: 'USD Coin' },
  ];

  return (
    <div className="min-h-screen flex flex-col template-preview-scale" style={{ backgroundColor: theme.bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <BrowserFrame />
      
      {/* Main Content (Website) */}
      <div className="flex-1 flex bg-[#f5f7fa]">
        {/* Sidebar */}
        <aside className={`${menuCollapsed ? 'w-16' : 'w-56'} bg-[#303646] flex flex-col transition-all duration-300 flex-shrink-0`}>
          {/* Logo */}
          <div className="p-4 flex items-center gap-3">
            <img 
              src="/cashinout-logo.png" 
              alt="Cashinout" 
              className={`${menuCollapsed ? 'w-8' : 'h-8'} object-contain`}
            />
          </div>

          {/* Main Menu */}
          <nav className="flex-1 py-2 overflow-y-auto">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                href="#"
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  item.active 
                    ? 'text-white bg-[#3d4557]' 
                    : 'text-[#9ca3af] hover:bg-[#3d4557] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* App Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-4">
              <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Приложение</span>
            </div>
          )}
          {appItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* For Business Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-4">
              <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Для бизнеса</span>
            </div>
          )}
          {businessItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* Bottom Items */}
          <div className="mt-4 border-t border-[#3d4557] pt-2">
            {bottomItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}
          </div>
        </nav>

        {/* Hide Menu Toggle */}
        <button 
          onClick={() => setMenuCollapsed(!menuCollapsed)}
          className="flex items-center gap-3 px-4 py-3 text-sm text-[#9ca3af] hover:bg-[#3d4557] border-t border-[#3d4557]"
        >
          <div className={`w-5 h-3 rounded-full bg-[#4a5568] flex items-center px-0.5 transition-all ${menuCollapsed ? 'justify-end' : 'justify-start'}`}>
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
          {!menuCollapsed && <span>Скрыть меню</span>}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
          {/* Language */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-lg">🇷🇺</span>
            <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
          </div>
          
          {/* User Name */}
          <span className="text-sm text-gray-700 font-medium">{accountName}</span>
          
          {/* User Avatar */}
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          
          {/* ID */}
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <span>ID:</span>
            <span className="font-medium">{userId}</span>
          </div>
          
          {/* Icons */}
          <button className="p-2 text-gray-500 hover:text-gray-700">
            <Copy className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="flex gap-6">
            {/* Left Column */}
            <div className="flex-1 min-w-0">
              {/* Page Title */}
              <div className="flex items-center gap-3 mb-6">
                <h1 className="text-2xl font-semibold text-gray-900">Главная</h1>
                <div className="flex items-center gap-1 text-green-500 text-sm">
                  <span>Аккаунт верифицирован</span>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>
              </div>

              {/* Wallet Card */}
              <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] rounded-xl p-5 mb-6">
                <h2 className="text-white font-medium text-lg mb-2">Мой Кошелек</h2>
                <p className="text-gray-300 text-sm mb-1">Основной баланс в USDT</p>
                <div className="flex items-center gap-2">
                  <span className="text-white text-3xl font-bold">{balances.USDT || '0'}</span>
                  <span className="text-white text-xl">USDT</span>
                  <button className="ml-2 text-gray-300 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-4">
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                    <ArrowLeftRight className="w-4 h-4" />
                    <span>Обмен</span>
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                    <Send className="w-4 h-4" />
                    <span>Отправить</span>
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                    <ArrowUpCircle className="w-4 h-4" />
                    <span>Получить</span>
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-[#3b82f6] hover:bg-[#2563eb] rounded-lg text-white text-sm transition-colors ml-auto">
                    <CreditCard className="w-4 h-4" />
                    <span>Оплатить</span>
                  </button>
                </div>
              </div>

              {/* My Accounts */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <h3 className="font-medium text-gray-900">Мои аккаунты</h3>
                  <button className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1">
                    Скрыть нулевые балансы
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                </div>
                <div className="divide-y divide-gray-100">
                  {cryptoList.map((crypto) => (
                    <div key={crypto.symbol} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <CryptoIcon symbol={crypto.symbol} />
                        <div>
                          <div className="font-medium text-gray-900">{crypto.symbol}</div>
                          <div className="text-xs text-gray-500">{crypto.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">{balances[crypto.symbol] || '0'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - News */}
            <div className="w-96 flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Новости</h3>
              
              {/* News Cards */}
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-purple-600 to-purple-500 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm mb-1">ОПЛАЧИВАЙ STEAM ПРЯМО ИЗ</h4>
                      <p className="text-sm opacity-90">ЛИЧНОГО КАБИНЕТА CASHINOUT</p>
                    </div>
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🎮</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm mb-1">ЧИТАЙ СВЕЖИЕ НОВОСТИ</h4>
                      <p className="text-sm opacity-90">В НАШЕМ ТГ-КАНАЛЕ</p>
                    </div>
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📱</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm mb-1">СКАЧИВАЙ ПРИЛОЖЕНИЕ</h4>
                      <p className="text-sm opacity-90">В GOOGLE PLAY</p>
                    </div>
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                      alt="Google Play"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              {/* Info Cards */}
              <div className="mt-6 space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">🛒</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-2">Платите в магазинах по QR коду сразу из крипты</h4>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Теперь можно не выводить криптовалюту в фиат, а платить в оффлайн магазинах сразу из нашего кошелька. 
                        Фактически вы платите в рублях на кассе, а мы очень быстро обеспечиваем конвертацию из USDT в рубли.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-xl">💳</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-2">Виртуальные карты для оплат сервисов по всему миру</h4>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Теперь у нас карты с 3d secure работают стабильнее и позволяют оплачивать тысячи сервисов: 
                        ChatGPT, Midjourney, Netflix, Canva, GoogleAds, Facebook, Perplexity, RenderAI и тп. 
                        Открываются за пару секунд.
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Нашими картами нельзя платить в рублях и валютах санкционных стран.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
    </div>
  );
}
